import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, sanitizeImageUrl } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import { formatTimestamp } from '../utils/formatTime';

const BADGE_ICONS = {
  sharpshooter:    '🎯',
  iron_wall:       '🛡️',
  season_champion: '🏆',
  war_hero:        '⚔️',
  streak_master:   '🔥',
};

const BADGE_NAMES = {
  sharpshooter:    'Sharpshooter',
  iron_wall:       'Iron Wall',
  season_champion: 'Season Champion',
  war_hero:        'War Hero',
  streak_master:   'Streak Master',
};

const BADGE_COLORS = {
  sharpshooter:    { bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.45)', glow: '#fbbf24' },
  iron_wall:       { bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.45)', glow: '#60a5fa' },
  season_champion: { bg: 'rgba(168,85,247,0.12)', border: 'rgba(168,85,247,0.45)', glow: '#a855f7' },
  war_hero:        { bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.45)',  glow: '#ef4444' },
  streak_master:   { bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.45)', glow: '#f97316' },
};

function formatSeasonLabel(season, t) {
  if (!season) return '';
  const [year, month] = season.split('-');
  const monthName = t('month_' + parseInt(month, 10));
  return `${monthName} ${year}`;
}

export default function PlayerProfile({ slug, playerTag, t, isModal, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showScrollHint, setShowScrollHint] = useState(false);
  const tabsWrapperRef = useRef(null);
  const tabsRef = useRef(null);

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['player_profile', slug, playerTag],
    queryFn: () => apiGet(`/v1/clan/${slug}/player/${playerTag}`),
    enabled: !!slug && !!playerTag,
  });

  useEffect(() => {
    const checkScrollable = () => {
      if (tabsRef.current && tabsWrapperRef.current) {
        const isScrollable = tabsRef.current.scrollWidth > tabsWrapperRef.current.clientWidth;
        const isScrolledToEnd = tabsRef.current.scrollLeft + tabsRef.current.clientWidth >= tabsRef.current.scrollWidth - 1;
        setShowScrollHint(isScrollable && !isScrolledToEnd);
      }
    };

    checkScrollable();
    const tabsEl = tabsRef.current;
    if (tabsEl) {
      tabsEl.addEventListener('scroll', checkScrollable, { passive: true });
      window.addEventListener('resize', checkScrollable, { passive: true });
    }

    return () => {
      if (tabsEl) {
        tabsEl.removeEventListener('scroll', checkScrollable);
      }
      window.removeEventListener('resize', checkScrollable);
    };
  }, [profile]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">😕</div>
        <h2 className="text-xl font-semibold text-gray-300 mb-2">{t('player_not_found') || 'Player Not Found'}</h2>
        <p className="text-gray-500 mb-6">{t('player_not_found_desc') || 'This player does not exist in this clan.'}</p>
        <Link
          to={`/clan/${slug}`}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
        >
          {t('back_to_dashboard') || 'Back to Dashboard'}
        </Link>
      </div>
    );
  }

  if (!profile) return null;

  const { player, lifetime_stats, season_history, war_log, badges, badge_counts, current_season } = profile;

  return (
    <div className="player-profile">
      {/* Header */}
      <div className="pp-header">
        {!isModal && (
          <div className="flex items-center justify-between mb-4">
            <Link
              to={`/clan/${slug}`}
              className="text-gray-400 hover:text-gray-200 flex items-center gap-2 transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              {t('back_to_dashboard') || 'Back to Dashboard'}
            </Link>
          </div>
        )}

        <div className="pp-header-content">
          <div className="pp-avatar">
            <img src={sanitizeImageUrl(player.league_icon)} alt="" className="pp-league-icon" />
          </div>
          <div className="pp-info">
            <div className="pp-name-row">
              <h1 className="pp-name">{player.name}</h1>
            </div>
            <div className="pp-tag">{player.tag}</div>
            <div className="pp-meta">
              <span className="pp-meta-item">
                <img
                  src={`${process.env.PUBLIC_URL}/static/img/townhall/TH${player.townhall}.webp`}
                  alt=""
                  className="w-4 h-4"
                />
                {t('col_townhall')} {player.townhall}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Badge Showcase — always visible below header */}
      {Object.keys(badge_counts).length > 0 && (
        <div className="pp-badge-showcase pp-badge-showcase--header">
          {Object.entries(badge_counts).map(([key, count]) => {
            const col = BADGE_COLORS[key] || { bg: 'rgba(255,255,255,0.08)', border: 'rgba(255,255,255,0.2)', glow: '#fff' };
            return (
              <div key={key} className="pp-badge-showcase-card" style={{ '--glow': col.glow, background: col.bg, borderColor: col.border }}>
                <div className="pp-badge-showcase-icon">{BADGE_ICONS[key]}</div>
                <div className="pp-badge-showcase-name">{BADGE_NAMES[key]}</div>
                <div className="pp-badge-showcase-count">×{count}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lifetime Stats Cards */}
      <div className="pp-stats-grid">
        <div className="pp-stat-card">
          <div className="pp-stat-icon">⚔️</div>
          <div className="pp-stat-value">{lifetime_stats.total_attacks}</div>
          <div className="pp-stat-label">{t('total_attacks') || 'Total Attacks'}</div>
        </div>
        <div className="pp-stat-card">
          <div className="pp-stat-icon">🏆</div>
          <div className="pp-stat-value">{lifetime_stats.total_points}</div>
          <div className="pp-stat-label">{t('total_points') || 'Total Points'}</div>
        </div>
        <div className="pp-stat-card">
          <div className="pp-stat-icon">⭐</div>
          <div className="pp-stat-value">{lifetime_stats.total_stars_earned}</div>
          <div className="pp-stat-label">{t('total_stars_earned') || 'Stars Earned'}</div>
        </div>
        <div className="pp-stat-card">
          <div className="pp-stat-icon">🔥</div>
          <div className="pp-stat-value">{lifetime_stats.longest_3_star_streak}</div>
          <div className="pp-stat-label">{t('best_streak') || 'Best 3★ Streak'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div
        ref={tabsWrapperRef}
        className={`pp-tabs-wrapper${showScrollHint ? ' show-scroll-hint' : ''}`}
      >
        <div ref={tabsRef} className="pp-tabs">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pp-tab ${activeTab === 'overview' ? 'active' : ''}`}
          >
            {t('tab_overview') || 'Overview'}
          </button>
          <button
            onClick={() => setActiveTab('seasons')}
            className={`pp-tab ${activeTab === 'seasons' ? 'active' : ''}`}
          >
            {t('tab_seasons') || 'Season History'}
          </button>
          <button
            onClick={() => setActiveTab('war_log')}
            className={`pp-tab ${activeTab === 'war_log' ? 'active' : ''}`}
          >
            {t('tab_war_log') || 'War Log'} ({war_log.length})
          </button>
          <button
            onClick={() => setActiveTab('badges')}
            className={`pp-tab ${activeTab === 'badges' ? 'active' : ''}`}
          >
            {t('tab_badges') || 'Badges'} ({badges.length})
          </button>
        </div>
        <div className="pp-tabs-scroll-indicator" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
      </div>

      {/* Tab Content */}
      <div className="pp-tab-content">
        {activeTab === 'overview' && (
          <OverviewTab
            lifetime_stats={lifetime_stats}
            season_history={season_history}
            badges={badges}
            badge_counts={badge_counts}
            t={t}
          />
        )}
        {activeTab === 'seasons' && (
          <SeasonsTab season_history={season_history} t={t} />
        )}
        {activeTab === 'war_log' && (
          <WarLogTab war_log={war_log} t={t} current_season={current_season} />
        )}
        {activeTab === 'badges' && (
          <BadgesTab badges={badges} badge_counts={badge_counts} t={t} />
        )}
      </div>
    </div>
  );
}

function OverviewTab({ lifetime_stats, season_history, badges, badge_counts, t }) {
  // Find best season
  const bestSeason = season_history.length > 0
    ? season_history.reduce((best, current) => current.points > best.points ? current : best)
    : null;

  return (
    <div className="pp-overview">
      {/* Lifetime Stats with subsections */}
      <div className="pp-section">
        <h3 className="pp-section-title">📊 {t('lifetime_stats') || 'Lifetime Stats'}</h3>

        {/* Attack Stats */}
        <div className="pp-subsection">
          <h4 className="pp-subsection-title">⚔️ {t('attack_stats') || 'Attack Stats'}</h4>
          <div className="pp-detail-grid">
            <div className="pp-detail-item">
              <span className="pp-detail-label">{t('total_3_stars') || '3-Star Attacks'}</span>
              <span className="pp-detail-value">{lifetime_stats.total_3_stars}</span>
            </div>
            <div className="pp-detail-item">
              <span className="pp-detail-label">{t('total_2_stars') || '2-Star Attacks'}</span>
              <span className="pp-detail-value">{lifetime_stats.total_2_stars}</span>
            </div>
            <div className="pp-detail-item">
              <span className="pp-detail-label">{t('low_star_attacks') || '0-1 Star Attacks'}</span>
              <span className="pp-detail-value">{lifetime_stats.low_star_attacks}</span>
            </div>
            <div className="pp-detail-item">
              <span className="pp-detail-label">{t('missed_attacks') || 'Missed Attacks'}</span>
              <span className="pp-detail-value">{lifetime_stats.missed_attacks}</span>
            </div>
          </div>
        </div>

        {/* Defense Stats */}
        <div className="pp-subsection">
          <h4 className="pp-subsection-title">🛡️ {t('defense_stats') || 'Defense Stats'}</h4>
          <div className="pp-detail-grid">
            <div className="pp-detail-item">
              <span className="pp-detail-label">{t('three_star_defenses') || '3-Star Defenses (Destroyed)'}</span>
              <span className="pp-detail-value">{lifetime_stats.three_star_defenses}</span>
            </div>
            <div className="pp-detail-item">
              <span className="pp-detail-label">{t('two_star_defenses') || '2-Star Defenses'}</span>
              <span className="pp-detail-value">{lifetime_stats.two_star_defenses}</span>
            </div>
            <div className="pp-detail-item">
              <span className="pp-detail-label">{t('low_star_defenses') || '0-1 Star Defenses (Good)'}</span>
              <span className="pp-detail-value">{lifetime_stats.low_star_defenses}</span>
            </div>
            <div className="pp-detail-item">
              <span className="pp-detail-label">{t('total_defenses') || 'Total Defenses'}</span>
              <span className="pp-detail-value">{lifetime_stats.total_defenses}</span>
            </div>
          </div>
        </div>

        {/* Other Stats - Streaks & Contributions */}
        <div className="pp-subsection">
          <h4 className="pp-subsection-title">✨ {t('other_stats') || 'Streaks & Contributions'}</h4>
          <div className="pp-detail-grid">
            <div className="pp-detail-item">
              <span className="pp-detail-label">{t('current_3_star_streak') || 'Current 3★ Streak'}</span>
              <span className="pp-detail-value">{lifetime_stats.current_3_star_streak}</span>
            </div>
            <div className="pp-detail-item">
              <span className="pp-detail-label">{t('total_donations') || 'Total Donations'}</span>
              <span className="pp-detail-value">{lifetime_stats.total_donations.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Best Season */}
      {bestSeason && (
        <div className="pp-section">
          <h3 className="pp-section-title">🏆 {t('best_season') || 'Best Season'}</h3>
          <div className="pp-best-season">
            <div className="pp-best-season-header">
              <div className="pp-best-season-name">{formatSeasonLabel(bestSeason.season, t)}</div>
              <div className="pp-best-season-points">
                <span className="pp-best-points-value">{bestSeason.points}</span>
                <span className="pp-best-points-label">{t('points') || 'pts'}</span>
              </div>
            </div>
            <div className="pp-best-season-stats">
              <div className="pp-best-stat-item">
                <span className="pp-best-stat-icon">⚔️</span>
                <span className="pp-best-stat-value">{bestSeason.attacks}</span>
                <span className="pp-best-stat-label">{t('attacks') || 'Attacks'}</span>
              </div>
              {bestSeason.three_star_pct !== null && (
                <div className="pp-best-stat-item">
                  <span className="pp-best-stat-icon">🎯</span>
                  <span className="pp-best-stat-value">{bestSeason.three_star_pct}%</span>
                  <span className="pp-best-stat-label">3★ {t('rate') || 'Rate'}</span>
                </div>
              )}
              {bestSeason.avg_stars_per_attack > 0 && (
                <div className="pp-best-stat-item">
                  <span className="pp-best-stat-icon">⭐</span>
                  <span className="pp-best-stat-value">{bestSeason.avg_stars_per_attack}</span>
                  <span className="pp-best-stat-label">{t('avg_stars') || 'Avg/Atk'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function SeasonsTab({ season_history, t }) {
  if (season_history.length === 0) {
    return (
      <div className="pp-empty">
        <div className="text-4xl mb-4">📅</div>
        <p>{t('no_season_history') || 'No season history available.'}</p>
      </div>
    );
  }

  return (
    <div className="pp-seasons">
      <div className="pp-seasons-table-wrapper">
        <table className="pp-seasons-table">
          <thead>
            <tr>
              <th>{t('season') || 'Season'}</th>
              <th>{t('points') || 'Points'}</th>
              <th>{t('attacks') || 'Attacks'}</th>
              <th>{t('defenses') || 'Defenses'}</th>
              <th>3★ %</th>
              <th>{t('avg_stars') || 'Avg Stars'}</th>
            </tr>
          </thead>
          <tbody>
            {season_history.map((season) => (
              <tr key={season.season}>
                <td className="pp-season-name">{formatSeasonLabel(season.season, t)}</td>
                <td className="pp-season-points">{season.points}</td>
                <td>{season.attacks}</td>
                <td>{season.defenses}</td>
                <td>
                  {season.three_star_pct !== null ? `${season.three_star_pct}%` : '-'}
                </td>
                <td>{season.avg_stars_per_attack} ⭐</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WarLogTab({ war_log, t, current_season }) {
  if (war_log.length === 0) {
    return (
      <div className="pp-empty">
        <div className="text-4xl mb-4">⚔️</div>
        <p>{t('no_war_log') || 'No war activity this season.'}</p>
        <p className="text-sm text-gray-500 mt-2">
          {t('war_log_note') || `Showing current season (${current_season}). War data is cleaned up after each season.`}
        </p>
      </div>
    );
  }

  return (
    <div className="pp-war-log">
      <p className="pp-war-log-note">
        {t('war_log_note') || `Showing current season (${current_season}). War data is cleaned up after each season.`}
      </p>
      <div className="pp-war-entries">
        {war_log.map((entry, i) => (
          <div key={i} className={`pp-war-entry ${entry.type}`}>
            <div className="pp-war-entry-icon">
              {entry.type === 'attack' ? '⚔️' : '🛡️'}
            </div>
            <div className="pp-war-entry-content">
              {entry.type === 'attack' ? (
                <>
                  <div className="pp-war-entry-header">
                    <span className="pp-war-entry-type">
                      {entry.war_type === 'CWL' ? 'CWL' : 'CW'} {t('attack') || 'Attack'}
                    </span>
                    <span className="pp-war-entry-stars">
                      {'⭐'.repeat(entry.stars)}{'☆'.repeat(3 - entry.stars)}
                    </span>
                  </div>
                  <div className="pp-war-entry-details">
                    <span className="pp-war-entry-th">
                      <img
                        src={`${process.env.PUBLIC_URL}/static/img/townhall/TH${entry.attacker_th}.webp`}
                        alt=""
                        className="w-5 h-5"
                      />
                      vs
                      <img
                        src={`${process.env.PUBLIC_URL}/static/img/townhall/TH${entry.defender_th}.webp`}
                        alt=""
                        className="w-5 h-5"
                      />
                    </span>
                    <span className="pp-war-entry-destruction">{entry.destruction}%</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="pp-war-entry-header">
                    <span className="pp-war-entry-type">
                      {entry.war_type === 'CWL' ? 'CWL' : 'CW'} {t('defense') || 'Defense'}
                    </span>
                    <span className="pp-war-entry-stars-conceded">
                      {'⭐'.repeat(entry.stars_conceded)}<span className="stars-empty">{'☆'.repeat(3 - entry.stars_conceded)}</span>
                    </span>
                  </div>
                  <div className="pp-war-entry-details">
                    <span className="pp-war-entry-th">
                      <img
                        src={`${process.env.PUBLIC_URL}/static/img/townhall/TH${entry.defender_th}.webp`}
                        alt=""
                        className="w-5 h-5"
                      />
                      vs
                      <img
                        src={`${process.env.PUBLIC_URL}/static/img/townhall/TH${entry.attacker_th}.webp`}
                        alt=""
                        className="w-5 h-5"
                      />
                    </span>
                    <span className="pp-war-entry-destruction">{entry.destruction}%</span>
                  </div>
                </>
              )}
            </div>
            <div className="pp-war-entry-time">
              {entry.timestamp && formatTimestamp(entry.timestamp)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const BADGE_CRITERIA = [
  { key: 'sharpshooter',    icon: '🎯', name: 'Sharpshooter',    desc: '80%+ 3-star rate in a season (min 10 attacks)' },
  { key: 'iron_wall',       icon: '🛡️', name: 'Iron Wall',       desc: '<1.7 avg stars conceded per defense in a season (min 10 defenses)' },
  { key: 'season_champion', icon: '🏆', name: 'Season Champion', desc: 'Finished #1 in season points' },
  { key: 'war_hero',        icon: '⚔️', name: 'War Hero',        desc: '3-starred both attacks in 5+ wars within a single season' },
  { key: 'streak_master',   icon: '🔥', name: 'Streak Master',   desc: '5+ consecutive 3-star attacks within a season (streak resets each season)' },
];

function BadgesTab({ badges, badge_counts, t }) {
  if (badges.length === 0) {
    return (
      <div className="pp-empty">
        <div className="text-4xl mb-4">🎖️</div>
        <p>{t('no_badges') || 'No badges earned yet.'}</p>
        <div className="pp-badge-criteria-list">
          {BADGE_CRITERIA.map(b => (
            <div key={b.key} className="pp-badge-criteria-item">
              <span>{b.icon}</span>
              <div><strong>{b.name}:</strong> {b.desc}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="pp-badges-tab">
      {/* Timeline list */}
      <div className="pp-section">
        <h3 className="pp-section-title">📜 {t('badge_history') || 'Badge History'}</h3>
        <div className="pp-badge-timeline">
          {badges.map((badge, i) => {
            const col = BADGE_COLORS[badge.badge] || { bg: 'rgba(255,255,255,0.08)', border: 'rgba(255,255,255,0.2)', glow: '#fff' };
            return (
              <div key={i} className="pp-badge-timeline-item">
                <div className="pp-badge-timeline-dot" style={{ background: col.glow, boxShadow: `0 0 8px ${col.glow}` }} />
                <div className="pp-badge-timeline-icon" style={{ background: col.bg, borderColor: col.border }}>
                  {badge.icon}
                </div>
                <div className="pp-badge-timeline-body">
                  <div className="pp-badge-timeline-name">{badge.name}</div>
                  <div className="pp-badge-timeline-detail">{badge.detail}</div>
                  {badge.season && (
                    <div className="pp-badge-timeline-season">{formatSeasonLabel(badge.season, t)}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* How to earn */}
      <div className="pp-section">
        <h3 className="pp-section-title">ℹ️ {t('how_to_earn') || 'How to Earn Badges'}</h3>
        <div className="pp-badge-criteria-list">
          {BADGE_CRITERIA.map(b => (
            <div key={b.key} className="pp-badge-criteria-item">
              <span>{b.icon}</span>
              <div><strong>{b.name}:</strong> {b.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
